// CppNewDialog — create a new Construction Phase Plan or Pre-Construction
// Information record (Phase 1 CPP/PCI generator). Mirrors RamsIssueDialog's
// client/project picker pattern.
import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAssignRecipients, AssignRecipientsStep } from "@/components/AssignRecipientsStep";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

export default function CppNewDialog({ presetClientId, presetProjectId, initialDocType = "cpp", onClose, onCreated }) {
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [clientId, setClientId] = useState(presetClientId || "");
    const [projectId, setProjectId] = useState(presetProjectId || "");
    const [docType, setDocType] = useState(initialDocType);
    const [title, setTitle] = useState("");
    const [saving, setSaving] = useState(false);
    const assign = useAssignRecipients(clientId);

    useEffect(() => {
        if (!presetClientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
    }, [presetClientId]);

    useEffect(() => {
        if (!clientId) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: clientId } }).then((r) => setProjects(r.data)).catch(() => { });
    }, [clientId]);

    const create = async () => {
        if (!clientId || !projectId) { toast.error("Client and project are required"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/cpp", {
                client_id: clientId, project_id: projectId, doc_type: docType, title: title || undefined,
                assigned_recipients: assign.recipients.length ? assign.recipients : undefined,
                assigned_message: assign.message.trim() || undefined,
            });
            toast.success(`${data.reference} created`);
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create record"); }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cpp-new-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4 max-h-[88vh] overflow-y-auto" style={{ border: "1px solid #E5E0DA" }}>
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#1F1F1F]">New CPP / PCI</h3>
                    <button data-testid="cpp-new-close" onClick={onClose}><X size={18} className="text-[#9A968F]" /></button>
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Document type</label>
                    <div className="flex gap-2 mt-1">
                        {["cpp", "pci"].map((t) => (
                            <button key={t} data-testid={`cpp-new-type-${t}`} onClick={() => setDocType(t)}
                                className={`flex-1 h-9 rounded-lg border text-sm font-semibold ${docType === t ? "text-white" : "bg-white text-[#6F6A64]"}`}
                                style={docType === t ? { background: "var(--brand-accent,#C75A1B)", borderColor: "var(--brand-accent,#C75A1B)" } : { borderColor: "#E5E0DA" }}>
                                {t === "cpp" ? "Construction Phase Plan" : "Pre-Construction Info"}
                            </button>
                        ))}
                    </div>
                </div>
                {!presetClientId && (
                    <div>
                        <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Client</label>
                        <select data-testid="cpp-new-client" className={inputCls} value={clientId}
                            onChange={(e) => { setClientId(e.target.value); setProjectId(""); }}>
                            <option value="">Select client…</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                )}
                {!presetProjectId && (
                    <div>
                        <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Project</label>
                        <select data-testid="cpp-new-project" className={inputCls} value={projectId} disabled={!clientId}
                            onChange={(e) => setProjectId(e.target.value)}>
                            <option value="">Select project…</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}
                <div>
                    <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Title (optional)</label>
                    <input data-testid="cpp-new-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder="Leave blank to auto-generate" />
                </div>
                <AssignRecipientsStep state={assign} testIdPrefix="cpp-new-assign" />
                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="cpp-new-cancel" onClick={onClose}
                        className="h-9 px-4 rounded-lg border border-[#E5E0DA] text-sm font-semibold text-[#6F6A64]">Cancel</button>
                    <button data-testid="cpp-new-create" onClick={create} disabled={saving}
                        className="h-9 px-4 rounded-lg text-sm font-semibold text-white inline-flex items-center gap-1.5"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {saving && <Loader2 size={13} className="animate-spin" />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}
